import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { getHotPotato, getHotPotatoReactions, hotPotatoGraphicUrl, toggleHotPotatoReaction } from '../api/hotPotato';
import { getAllowedEmojis } from '../api/reactions';
import { getAvatar } from '../api/avatars';
import { getAccount } from '../api/auth';
import Avatar from '../components/Avatar';
import ReactionBar from '../components/ReactionBar';
import { HotPotatoIcon } from '../assets/icons';
import { fetchDefaultAvatarSvg } from '../utils';
import '../styles/HotPotato.css';

const formatSentAt = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
};

const HotPotatoViewPage = () => {
    const { hotPotatoId } = useParams();
    const history = useHistory();
    const [hotPotato, setHotPotato] = useState(null);
    const [avatars, setAvatars] = useState({});
    const [myId, setMyId] = useState('');
    const [reactions, setReactions] = useState([]);
    const [allowedEmojis, setAllowedEmojis] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let mounted = true;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const [data, account, emojis, reactionData] = await Promise.all([
                    getHotPotato(hotPotatoId),
                    getAccount().catch(() => null),
                    getAllowedEmojis().catch(() => []),
                    getHotPotatoReactions(hotPotatoId).catch(() => ({ reactions: [] })),
                ]);
                if (!mounted) return;
                setHotPotato(data);
                setMyId(account?.user?.auth_id || '');
                setAllowedEmojis(emojis || []);
                setReactions(reactionData.reactions || []);

                const fallback = await fetchDefaultAvatarSvg().catch(() => '');
                const ids = [...new Set([data.sender_avatar_id, data.recipient_avatar_id].filter(Boolean))];
                const entries = await Promise.all(ids.map(async (id) => {
                    try {
                        const result = await getAvatar(id);
                        return [id, result.avatar?.svg_data || fallback];
                    } catch {
                        return [id, fallback];
                    }
                }));
                if (!mounted) return;
                const next = { default: fallback };
                entries.forEach(([id, svg]) => { next[id] = svg; });
                setAvatars(next);
            } catch (err) {
                if (mounted) setError(err.message || 'Hot Potato not found');
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, [hotPotatoId]);

    const avatarFor = (id) => avatars[id] || avatars.default || '';

    const refreshReactions = async () => {
        try {
            const data = await getHotPotatoReactions(hotPotatoId);
            setReactions(data.reactions || []);
        } catch (err) {
            console.error('Failed to refresh reactions:', err);
        }
    };

    const renderShell = (children) => (
        <div className="sc-page hp-page">
            <div className="sc-container">
                <header className="sc-header hp-header">
                    <button type="button" className="sc-back-link" onClick={() => history.goBack()}>
                        ← Back
                    </button>
                    <h2>
                        <HotPotatoIcon aria-hidden="true" className="hp-title-icon" />
                        Hot Potato
                    </h2>
                </header>
                {children}
            </div>
        </div>
    );

    if (loading) {
        return renderShell(
            <div className="sc-loading">
                <div className="sc-spinner" />
                <p>Unwrapping…</p>
            </div>
        );
    }

    if (error || !hotPotato) {
        return renderShell(
            <div className="hp-body">
                <div className="sc-alert sc-alert--error" role="alert">{error || 'Hot Potato not found'}</div>
            </div>
        );
    }

    const isRecipient = Boolean(myId) && hotPotato.recipient_id === myId;
    const isSender = Boolean(myId) && hotPotato.sender_id === myId;
    const headline = isRecipient
        ? `${hotPotato.sender_username || 'A friend'} sent you a Hot Potato!`
        : isSender
            ? `You sent ${hotPotato.recipient_username || 'a friend'} a Hot Potato`
            : 'Hot Potato';

    return renderShell(
        <div className="hp-body">
            <p className="hp-headline">{headline}</p>

            <div className="hp-preview hp-preview--full">
                <img
                    className="hp-preview-graphic hp-preview-graphic--lg"
                    src={hotPotatoGraphicUrl(hotPotato.graphic)}
                    alt=""
                />
                <p className="hp-preview-message hp-preview-message--lg">{hotPotato.message}</p>
                <ReactionBar
                    key={hotPotato.id}
                    activityId={hotPotato.id}
                    reactions={reactions}
                    currentUserId={myId}
                    allowedEmojis={allowedEmojis}
                    onToggle={(emoji) => toggleHotPotatoReaction(hotPotato.id, emoji)}
                    onReactionChange={refreshReactions}
                />
            </div>

            <div className="hp-parties">
                <button
                    type="button"
                    className="hp-party"
                    onClick={() => hotPotato.sender_username && history.push(`/user/${hotPotato.sender_username}`)}
                >
                    <Avatar svgContent={avatarFor(hotPotato.sender_avatar_id)} alt="" className="hp-party-avatar" />
                    <span className="hp-party-text">
                        <span className="hp-party-role">From</span>
                        <span className="hp-party-name">{hotPotato.sender_username || 'Unknown'}</span>
                    </span>
                </button>
                <span className="hp-party-arrow" aria-hidden="true">→</span>
                <button
                    type="button"
                    className="hp-party"
                    onClick={() => hotPotato.recipient_username && history.push(`/user/${hotPotato.recipient_username}`)}
                >
                    <Avatar svgContent={avatarFor(hotPotato.recipient_avatar_id)} alt="" className="hp-party-avatar" />
                    <span className="hp-party-text">
                        <span className="hp-party-role">To</span>
                        <span className="hp-party-name">{hotPotato.recipient_username || 'Unknown'}</span>
                    </span>
                </button>
            </div>

            <p className="hp-meta">Sent {formatSentAt(hotPotato.created_at)}</p>

            <div className="hp-actions">
                {isRecipient && hotPotato.sender_username && (
                    <button
                        type="button"
                        className="sc-btn sc-btn--primary"
                        onClick={() => history.push(`/hot-potato/send?to=${encodeURIComponent(hotPotato.sender_username)}`)}
                    >
                        <HotPotatoIcon aria-hidden="true" />
                        Toss one back
                    </button>
                )}
                <button type="button" className="sc-btn sc-btn--secondary" onClick={() => history.push('/hot-potato')}>
                    My Hot Potatoes
                </button>
            </div>
        </div>
    );
};

export default HotPotatoViewPage;
